<template>
  <ion-item
    detail="true" data-testid="lesson-item" :data-lesson-id="lesson.id"
    class="lesson-card"
    lines="none" button="true"
  >
    <user-avatar
      v-if="displayUser" slot="start" :url="userAvatarUrl"
      :name="lesson[otherPartyName].name"
    />
    <lesson-status
      v-if="!displayUser" slot="start" :lesson="lesson"
      :display-text="false" :display-notes-icon="true"
    />
    <ion-label class="lesson-card__label">
      <div class="lesson-card__top">
        <div class="lesson-card__title-block">
          <div v-if="displayUser" class="lesson-card__name">{{ lesson[otherPartyName].name }}</div>
          <div class="lesson-card__datetime">{{ lesson.formattedDate }}</div>
          <div class="lesson-card__details">{{ displayFullLength(lesson.time, lesson.length) }}</div>
        </div>
        <div class="lesson-card__status" :class="{ 'lesson-card__status--with-notes': lesson.hasInstructorNotes() }">
          <lesson-status
            v-if="displayUser && !displayNotes"
            :lesson="lesson"
            :display-text="true"
            :display-notes-icon="true"
          />
        </div>
      </div>
      <div class="lesson-card__meta">
        <ion-note v-if="lesson.needToCollectPayment()" color="danger">{{ lesson.paymentStatus() }}</ion-note>
        <ion-note v-if="!lesson.needToCollectPayment() && !displayUser">{{ lesson.paymentStatus() }}</ion-note>
      </div>
      <NotesDisplay
        v-if="displayNotes && lesson.hasInstructorNotes()"
        class="lesson-card__notes"
        :notes="`${lesson.instructorNotes.slice(0, 100)}...`"
      />
    </ion-label>
    <ion-icon
      v-if="lesson.isAwaitingActionBy(currentUser)" slot="end" :icon="ellipse"
      data-testid="unread-icon" style="color: #1982FC; fill: #1982FC" size="small"
    />
  </ion-item>
</template>

<script setup>
import { IonIcon, IonItem, IonLabel, IonNote } from '@ionic/vue'
import { ellipse } from 'ionicons/icons'

import LessonStatus from '@/4_features/LessonStatus.vue'
import NotesDisplay from '@/shared/components/NotesDisplay.vue'
import UserAvatar from '@/3_widgets/UserAvatar.vue'
import { displayFullLength } from '@/6_shared/utils/formattingLessons'
import { useUserStore } from '@/stores/user'
const userStore = useUserStore()

const props = defineProps({
  lesson: { type: Object, required: true },
  currentUser: { type: String, required: true },
  displayUser: { type: Boolean, default: true },
  displayNotes: { type: Boolean, default: false },
})

const userAvatarUrl = props.currentUser === 'student' ? props.lesson.instructor.photoURL : props.lesson.student.photoURL

const otherPartyName = userStore.is.student ? 'instructor' : 'student'

</script>

<style scoped>
.lesson-card {
  --background: transparent;
  --border-radius: 16px;
  --padding-start: 0;
  --inner-padding-end: 0;
  --border-width: 0;
  --inner-border-width: 0;
}

.lesson-card__label {
  padding: 0.95rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.lesson-card__title-block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.lesson-card__name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #13261e;
  letter-spacing: 0.02em;
}

.lesson-card__datetime {
  font-size: 1rem;
  font-weight: 600;
  color: #0f422a;
}

.lesson-card__details {
  font-size: 0.9rem;
  color: rgba(19, 38, 30, 0.7);
  text-transform: none;
}

.lesson-card__status {
  align-items: center;
}

.lesson-card__status :deep(ion-chip) {
  --background: rgba(15, 66, 42, 0.08);
  --color: #0f422a;
  border-radius: 999px;
  padding: 0.35rem 0;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.lesson-card__status--with-notes :deep(ion-chip) {
  --background: rgba(185, 152, 99, 0.14);
  --color: #855f2d;
}

.lesson-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.lesson-card__meta ion-note {
  font-size: 0.9rem;
}

.lesson-card__role-tag {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(19, 38, 30, 0.08);
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(19, 38, 30, 0.7);
}

.lesson-card__notes {
  font-size: 0.9rem;
}
</style>
